import { useEffect, useState, useCallback } from 'react';
import type { InviteToken } from '@runninglime/shared';
import { apiGet, apiPost, apiDelete } from '../../lib/api.ts';
import { LoadingSpinner } from '../../components/ui/Shared.tsx';

export default function InviteManager() {
  const [invites, setInvites] = useState<InviteToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [expiresInDays, setExpiresInDays] = useState<number>(7);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'active' | 'used' | 'expired'>('all');

  const loadInvites = useCallback(async () => {
    const res = await apiGet<{ items: InviteToken[]; total: number }>('/admin/invites');
    if (res.success && res.data) {
      setInvites(res.data.items);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadInvites();
  }, [loadInvites]);

  const createInvite = async () => {
    setCreating(true);
    const res = await apiPost<InviteToken>('/admin/invites', {
      expiresInDays: expiresInDays > 0 ? expiresInDays : undefined,
    });
    if (res.success) {
      await loadInvites();
    }
    setCreating(false);
  };

  const revokeInvite = async (id: string) => {
    if (!confirm('Revoke this registration token? It will no longer be usable.')) return;
    await apiDelete(`/admin/invites/${id}`);
    await loadInvites();
  };

  const copyToken = async (token: string, id: string) => {
    await navigator.clipboard.writeText(token);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const getStatus = (invite: InviteToken): 'active' | 'used' | 'expired' | 'revoked' => {
    if (invite.usedBy) return 'used';
    if (!invite.active) return 'revoked';
    if (invite.expiresAt && new Date(invite.expiresAt) < new Date()) return 'expired';
    return 'active';
  };

  const statusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'used':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'expired':
        return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      case 'revoked':
        return 'bg-red-50 text-red-700 border-red-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const filteredInvites = invites.filter((inv) => {
    if (filter === 'all') return true;
    const status = getStatus(inv);
    if (filter === 'active') return status === 'active';
    if (filter === 'used') return status === 'used';
    if (filter === 'expired') return status === 'expired' || status === 'revoked';
    return true;
  });

  if (loading) return <LoadingSpinner />;

  return (
    <div className="animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-dark">Registration Tokens</h1>
          <p className="text-gray-500 text-sm mt-1">Generate invite tokens for new users to register</p>
        </div>
      </div>

      {/* Create New Token */}
      <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
        <h2 className="text-lg font-semibold text-dark mb-4">Generate New Token</h2>
        <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4">
          <div>
            <label htmlFor="expiresInDays" className="block text-sm font-medium text-dark mb-1.5">
              Expires in (days)
            </label>
            <input
              id="expiresInDays"
              type="number"
              min={0}
              value={expiresInDays}
              onChange={(e) => setExpiresInDays(parseInt(e.target.value) || 0)}
              className="w-32 px-4 py-2.5 rounded-xl border border-gray-200 focus:border-lime focus:ring-2 focus:ring-lime/30 outline-none transition-all bg-white text-sm"
              placeholder="0 = never"
            />
            <p className="text-xs text-gray-400 mt-1">Set 0 for no expiry</p>
          </div>
          <button
            onClick={createInvite}
            disabled={creating}
            className="btn-gradient px-6 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-60"
          >
            {creating ? 'Generating...' : 'Generate Token'}
          </button>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 mb-4">
        {(['all', 'active', 'used', 'expired'] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
              filter === f
                ? 'bg-dark text-white'
                : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {/* Tokens List */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        {filteredInvites.length === 0 ? (
          <div className="px-6 py-12 text-center text-gray-400 text-sm">
            {filter === 'all' ? 'No registration tokens generated yet' : `No ${filter} tokens`}
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {filteredInvites.map((invite) => {
              const status = getStatus(invite);
              return (
                <div key={invite.id} className="px-6 py-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <code className="text-sm font-mono text-dark bg-gray-100 px-2 py-0.5 rounded truncate max-w-[280px]">
                          {invite.token}
                        </code>
                        <button
                          onClick={() => copyToken(invite.token, invite.id)}
                          className="shrink-0 text-gray-400 hover:text-dark transition-colors"
                          title="Copy token"
                        >
                          {copiedId === invite.id ? (
                            <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                          ) : (
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9.75a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184" />
                            </svg>
                          )}
                        </button>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-gray-500">
                        <span>Created {new Date(invite.createdAt).toLocaleDateString()}</span>
                        {invite.expiresAt && (
                          <span>Expires {new Date(invite.expiresAt).toLocaleDateString()}</span>
                        )}
                        {invite.usedAt && (
                          <span>Used {new Date(invite.usedAt).toLocaleDateString()}</span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <span className={`px-2.5 py-0.5 text-xs rounded-full font-medium border ${statusBadge(status)}`}>
                        {status}
                      </span>
                      {status === 'active' && (
                        <button
                          onClick={() => revokeInvite(invite.id)}
                          className="text-red-500 hover:text-red-700 text-sm font-medium transition-colors"
                        >
                          Revoke
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
